import { useEffect, useState } from 'react';
import { getDemoResetStatus } from '../utils/api-helper';
import { wsSubscribe, wsUnsubscribe } from '../utils/wsManager';

// Channel the backend broadcasts demo reset progress on
export const DEMO_RESET_CHANNEL = { entityType: 'DemoReset', entityId: -1 };

const DEMO_RESET_STARTED_EVENT = 'demoResetStarted';

/**
 * Locks the app right after the reset request is accepted, without waiting for
 * the first broadcast to land.
 */
export const notifyDemoResetStarted = () => {
  window.dispatchEvent(new CustomEvent(DEMO_RESET_STARTED_EVENT));
};

/**
 * Subscribes every authenticated user to the demo reset channel, which sends
 * { inProgress: true/false }, and reports whether the app should be locked.
 *
 * `isFinished` only means anything while locked - the overlay stays up asking
 * for a refresh, since the data behind it is stale once a reset has run.
 *
 * @param {boolean} isAuthenticated
 * @returns {Object} { isLocked, isFinished }
 */
const useDemoResetLock = (isAuthenticated) => {
  const [isLocked, setIsLocked] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLocked(false);
      setIsFinished(false);
      return;
    }

    let wsKey = null;
    let isActive = true;

    const applyStatus = (inProgress) => {
      if (!isActive) return;
      setIsFinished(!inProgress);
      if (inProgress) setIsLocked(true);
    };

    const handleEvent = (event) =>
      applyStatus((event?.data ?? event)?.inProgress);
    const handleLocalStart = () => applyStatus(true);

    window.addEventListener(DEMO_RESET_STARTED_EVENT, handleLocalStart);

    (async () => {
      // A reset may already be running when this session loads - the socket
      // only delivers events from the moment we connect, so seed from the API
      try {
        const statusResp = await getDemoResetStatus();
        applyStatus(statusResp?.data?.inProgress);
      } catch {
        /* status is best effort here, the socket is the source of truth */
      }

      const sub = await wsSubscribe(DEMO_RESET_CHANNEL, {
        received: handleEvent
      });
      wsKey = sub?.key;
    })();

    return () => {
      isActive = false;
      window.removeEventListener(DEMO_RESET_STARTED_EVENT, handleLocalStart);
      if (wsKey) wsUnsubscribe(wsKey);
    };
  }, [isAuthenticated]);

  return { isLocked, isFinished };
};

export default useDemoResetLock;
